import featureFlag from '@/api/featureFlag';
import config from '@/config';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import reactiveCashflow from '@riseupil/reactive-cashflow';
import _ from 'lodash';
import consts from '../utilities/constants';

const state = {
  featureFlags: {},
  remoteVariables: {},
  featureFlagsJsons: {},
  localOverrides: {},
};

const LOCAL_STORAGE_OVERRIDE_PREFIX = 'feature_flag_';
const { FEATURE_FLAG_NAMES: featureFlagNames, REMOTE_VARIABLES: remoteVariables, FEATURE_FLAG_JSON_NAMES: featureFlagJsonNames } = consts;

const getters = {
  featureFlagAttributes: state => state.featureFlags,
  isStoryMenuOn: state => _isFlagOn(state, featureFlagNames.STORY_MENU),
  buildsFixedIncomeUsingMLModel: state => _isFlagOn(state, featureFlagNames.FIXED_INCOME_USING_ML_MODEL),
  noTrialDiscountIsOn: state => _isFlagOn(state, featureFlagNames.NO_TRIAL_DISCOUNT),
  israelBankInterest: state => parseFloat(_getVariableValue(state, remoteVariables.ISRAEL_BANK_INTEREST)),
  inflationRate: state => parseFloat(_getVariableValue(state, remoteVariables.INFLATION_RATE)),
  isFlagIsOn: state => _isFlagOn.bind(this, state),
  showExperimentalPages: state => _isFlagOn(state, featureFlagNames.AKIVA_STYLE),
  eoyMenuBannerSection: state => _isFlagOn(state, featureFlagNames.EOY_MENU_BANNER),
  showMislakaConsentScreen: state => _isFlagOn(state, featureFlagNames.SHOW_MISLAKA_CONSENT_SCREEN),
  newShortTermDormant: state => _isFlagOn(state, featureFlagNames.NEW_SHORT_TERM_DORMANT),
  pensionStory: state => _isFlagOn(state, featureFlagNames.PENSION_STORY),
  newSubscriptionCta: state => _isFlagOn(state, featureFlagNames.NEW_SUBSCRIPTION_CTA),
  offeringsExperiment: state => state.featureFlagsJsons[featureFlagJsonNames.OFFERINGS_EXPERIMENT]?.offerings ?? [],
  showTransactionOriginalAmount: state => _isFlagOn(state, featureFlagNames.SHOW_TRANSACTION_ORIGINAL_AMOUNT),
  incognitoModeDetection: state => _isFlagOn(state, featureFlagNames.INCOGNITO_MODE_DETECTION),
  openForEmployees: state => _isFlagOn(state, featureFlagNames.OPEN_FOR_EMPLOYEES),
  useNewCredNameText: state => _isFlagOn(state, featureFlagNames.NEW_CRED_NAME_INPUT),
  preventPoalimClosedBanking: state => _isFlagOn(state, featureFlagNames.PREVENT_POALIM_CLOSED_BANKING),
  openMeitavOBK: state => _isFlagOn(state, featureFlagNames.OPEN_MEITAV_OBK),
  openBudgetzOBK: state => _isFlagOn(state, featureFlagNames.OPEN_BUDGETZ_OBK),
  showLeumiChromeIphoneAlert: state => _isFlagOn(state, featureFlagNames.SHOW_LEUMI_CHROM_IPHONE_ALERT),
  showNewVouchersFlow: state => _isFlagOn(state, featureFlagNames.NEW_VOUCHERS_FLOW),
  paymentInitiationSupportedBanks: state => state.featureFlagsJsons[featureFlagJsonNames.PAYMENT_INITIATION_SUPPORTED_BANKS],
  otsIlPromptToStory: state => state.featureFlagsJsons[featureFlagJsonNames.OTSIL_PROMPT_TO_STORY],
  special2YearsOffering: state => _isFlagOn(state, featureFlagNames.SPECIAL_2_YEARS_OFFERING),
  showMobileAppPromptFeatureFlag: state => _isFlagOn(state, featureFlagNames.VIEW_APP_PROMPT),
  annualDiscountForDormants: state => _isFlagOn(state, featureFlagNames.ANNUAL_DISCOUNT_FOR_DORMANTS),
  preventPayments: state => _isFlagOn(state, featureFlagNames.PREVENT_PAYMENTS),
  enableAccountNickname: state => _isFlagOn(state, featureFlagNames.ACCOUNT_NICKNAME),
  show100RamiVoucher: state => _isFlagOn(state, featureFlagNames.SHOW_100_RAMI_VOUCHER),
  openOneZero: state => _isFlagOn(state, featureFlagNames.OPEN_ONE_ZERO),
  betaGroup: state => _isFlagOn(state, featureFlagNames.BETA_GROUP),
  showBetaGroupLogo: state => _isFlagOn(state, featureFlagNames.SHOW_BETA_GROUP_LOGO),
  showHachishuk: state => _isFlagOn(state, featureFlagNames.SHOW_HACHISHUK),
  useSplitLayerInV1: state => _isFlagOn(state, featureFlagNames.USE_SPLIT_LAYER_IN_V1),
  showMortgagePage: state => _isFlagOn(state, featureFlagNames.SHOW_MORTGAGE_PAGE),
  showIBIInvestmentsPage: state => _isFlagOn(state, featureFlagNames.SHOW_IBI_INVESTMENTS_PAGE),
  showTaxReturnPage: state => _isFlagOn(state, featureFlagNames.SHOW_TAX_RETURN_PAGE),
  showMortgageResultsPage: state => _isFlagOn(state, featureFlagNames.SHOW_MORTGAGE_RESULTS_PAGE),
  newAppBanner: state => _isFlagOn(state, featureFlagNames.NEW_APP_BANNER),
  newIosAppBanner: state => _isFlagOn(state, featureFlagNames.NEW_IOS_APP_BANNER),
  uploadMortgageData: state => _isFlagOn(state, featureFlagNames.UPLOAD_MORTGAGE_DATA),
  dailyRoutineStreakV2: state => _isFlagOn(state, featureFlagNames.DAILY_ROUTINE_STREAK_V2),
  excludedTotalAmount: state => _isFlagOn(state, featureFlagNames.EXCLUDED_TOTAL_AMOUNT),
  commentAndMoveTransaction: state => _isFlagOn(state, featureFlagNames.COMMEMT_AND_MOVE_TRANSACTION),
  expensesPredictAmount: state => _isFlagOn(state, featureFlagNames.EXPENSES_PREDICTED_AMOUNTS),
  customScheduleMessages: state => _isFlagOn(state, featureFlagNames.CUSTOM_SCHEDULE_MESSAGES),
  yearlyPromotion: state => _isFlagOn(state, featureFlagNames.YEARLY_PROMOTION),
  futureMonthNavigation: state => _isFlagOn(state, featureFlagNames.FUTURE_MONTH_NAVIGATION),
  showYearlyPromotionBanner: state => _isFlagOn(state, featureFlagNames.SHOW_YEARLY_PROMOTION_BANNER),
  showMonthlyPromotionBanner: state => _isFlagOn(state, featureFlagNames.SHOW_MONTHLY_PROMOTION_BANNER),
  appleBannerPriceIncrease: state => _isFlagOn(state, featureFlagNames.APPLE_BANNER_PRICE_INCREASE),
  priceIncrease: state => _isFlagOn(state, featureFlagNames.PRICE_INCREASE),
  dailyRoutineSettings: state => _isFlagOn(state, featureFlagNames.DAILY_ROUTINE_SETTINGS),
  newMonthlyUpgradeButton: state => _isFlagOn(state, featureFlagNames.NEW_MONTHLY_UPGRADE_BUTTON),
  showNewTrackingCategoryEdit: state => _isFlagOn(state, featureFlagNames.SHOW_NEW_TRACKING_CATEGORY_EDIT),
  subscriptionOfferingTmpFix: state => _isFlagOn(state, featureFlagNames.SUBSCRIPTION_OFFERING_TMP_FIX),
  subscriptionOfferingTmpFix390: state => _isFlagOn(state, featureFlagNames.SUBSCRIPTION_OFFERING_TMP_FIX_390),
  bestActionForTransaction: state => _isFlagOn(state, featureFlagNames.BEST_ACTION_FOR_TRANSACTION),
  cashflowChatbot: state => _isFlagOn(state, featureFlagNames.CASHFLOW_CHATBOT),
  pushNotifications: state => _isFlagOn(state, featureFlagNames.PUSH_NOTIFICATIONS),
  eoy2025Badge: state => _isFlagOn(state, featureFlagNames.EOY_2025_BADGE),
};

const actions = {
  async init({ commit, dispatch }) {
    const [featureFlagTreatments, remoteVariablesTreatments, featureFlagJsonTreatments] = await Promise.all([
      featureFlag.getTreatments(_.values(featureFlagNames)),
      featureFlag.getTreatments(_.values(remoteVariables)),
      featureFlag.getTreatmentsAsJson(_.values(featureFlagJsonNames)),
    ]);
    const featuresTreatments = _.zipObject(_.values(featureFlagNames), featureFlagTreatments);
    const variablesTreatments = _.zipObject(_.values(remoteVariables), remoteVariablesTreatments);
    commit('setFeatureFlags', featuresTreatments);
    commit('setRemoteVariables', variablesTreatments);
    commit('setFeatureFlagsJsons', featureFlagJsonTreatments);
    await dispatch('initLocalOverrides');
    reactiveCashflow.setFeatureFlags(featuresTreatments);
  },
  logTreatments({ state }, customerId) {
    DDLogs.log('Customer feature flags', { customerId, featureFlagAttributes: state.featureFlags });
    const featureFlags = localStorage.getItem('feature_flags');
    if (!featureFlags || !_.isEqual(JSON.parse(featureFlags), state.featureFlags)) {
      Segment.trackUserGot('BalanceMoneyInsightInTrial_FeatureFlagValue', {
        enabled: state.featureFlags[featureFlagNames.BALANCE_MONEY_INSIGHT_IN_TRIAL] === 'on',
      });
      localStorage.setItem('feature_flags', JSON.stringify(state.featureFlags));
    }
  },
  async isFeatureFlagOn({ state }, flagName) {
    return (await featureFlag.getTreatment(flagName)) === 'on' || state.localOverrides[flagName] === 'on';
  },
  async initLocalOverrides({ dispatch, commit }) {
    const localOverrides = Object.entries(localStorage)
      .filter(([key]) => key.startsWith(LOCAL_STORAGE_OVERRIDE_PREFIX))
      .reduce((features, [key, value]) => {
        const flagName = key.replace(LOCAL_STORAGE_OVERRIDE_PREFIX, '');
        features[flagName] = value;
        return features;
      }, {});
    commit('setLocalOverrides', localOverrides);
    await dispatch('initFeaturesExtension');
  },
  initFeaturesExtension({ state }) {
    try {
      window.chrome?.runtime?.sendMessage(
        config.get().featureFlagExtensionId,
        { features: state.featureFlags, localOverrides: state.localOverrides },
      );
    } catch (e) {
      DDLogs.error('Failed initializing features extension', e);
    }
  },
};

// mutations
const mutations = {
  setFeatureFlags(state, featureFlags) {
    state.featureFlags = featureFlags;
  },
  setRemoteVariables(state, variablesTreatments) {
    state.remoteVariables = variablesTreatments;
  },
  setLocalOverrides(state, localOverrides) {
    state.localOverrides = localOverrides;
  },
  setFeatureFlagsJsons(state, featureFlagsJsons) {
    state.featureFlagsJsons = featureFlagsJsons;
  },
};

function _isFlagOn(state, featureFlagName) {
  if (state.localOverrides[featureFlagName]) {
    return state.localOverrides[featureFlagName] === 'on';
  }
  return state.featureFlags[featureFlagName] === 'on';
}

function _getVariableValue(state, variableName) {
  return state.remoteVariables[variableName];
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
